/**
 * The §3.2 grammar as data: what you can pick up, where you can put it, and
 * which operation that drop is.
 *
 * A verb is `(held kind, target kind, modifiers) → operationId + Args`. The
 * same table serves the pointer, the click-to-carry path and the keyboard,
 * because all three end in one call to `verb.args(held, target, ctx)`: that
 * is how the three paths come to send identical Args. Every verb also carries
 * its §3.2 row, the pointer gesture and the keyboard path in words, so the
 * coverage test can hold the table to the brief and the verb ring can teach
 * the operation by name.
 *
 * Args are MCP `tools/call` arguments: parameters by name plus `body`. Slots
 * always travel as a lower-case hex mask. When a drop cannot fill a required
 * hole (a name still to be typed, say) the verb answers `{need, partial}` and
 * the deck opens the card with what the drop did fill.
 *
 * Held and Target `data` shapes are fixed here, by the `nouns` builders: a
 * screen that makes something liftable builds it with these so every verb can
 * read it.
 */

import { maskSlots, runs } from '../../../console/src/grid';
import type {
  Args, Catalogue, Held, HeldKind, Hole, Json, Modifier, Operation, Role, Target, TargetKind, Verb, VerbContext,
} from '../contracts';
import { predict } from '../model/predict';
import { confirmOf } from '../spec/confirm';
import { objectOf } from '../model/json';
import { hex, maskOf } from '../model/masks';

// ---------- reading Json data ----------

export type Obj = { readonly [k: string]: Json };
export const str = (o: Obj, k: string): string | undefined => (typeof o[k] === 'string' ? (o[k] as string) : undefined);
export const num = (o: Obj, k: string): number | undefined =>
  typeof o[k] === 'number' && Number.isFinite(o[k] as number) ? (o[k] as number) : undefined;
export const bool = (o: Obj, k: string): boolean | undefined => (typeof o[k] === 'boolean' ? (o[k] as boolean) : undefined);
export const strs = (o: Obj, k: string): string[] | undefined => {
  const v = o[k];
  return Array.isArray(v) && v.every((x) => typeof x === 'string') ? (v as string[]) : undefined;
};

// ---------- masks and words ----------

export const firstSlot = (mask: bigint): number => {
  if (mask <= 0n) return -1;
  let i = 0;
  while (((mask >> BigInt(i)) & 1n) === 0n) i += 1;
  return i;
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n: number): string => String(n).padStart(2, '0');

/** '2026-10-06' → 'Tue 6 Oct' (UTC, like the API). */
export const dayLabel = (date: string): string => {
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};
export const posLabel = (o: Obj): string => {
  const date = str(o, 'date');
  const hour = num(o, 'hour');
  if (!date) return '';
  return hour === undefined ? dayLabel(date) : `${dayLabel(date)} ${pad2(hour)}:00`;
};
export const slotsLabel = (hex: string | undefined): string => (hex ? runs(maskSlots(hex)) : '');
const words = (...parts: (string | undefined | false)[]): string => parts.filter((p) => p && p.length > 0).join(' ');

/** A position as body or query fields: `date` and, on a minute World, `hour`. */
const where = (o: Obj): Record<string, Json> => {
  const out: Record<string, Json> = {};
  const date = str(o, 'date');
  const hour = num(o, 'hour');
  if (date !== undefined) out.date = date;
  if (hour !== undefined) out.hour = hour;
  return out;
};

/** How a body names an occupier: holders by their unique name, events and series by id (their names repeat). */
export const whoOf = (o: Obj): Json => {
  const kind = str(o, 'kind');
  const name = str(o, 'name') ?? str(o, 'occupier');
  const id = num(o, 'id') ?? num(o, 'occupierId');
  if ((kind === 'event' || kind === 'series') && id !== undefined) return id;
  return name ?? id ?? null;
};
const nameOf = (o: Obj): string => str(o, 'name') ?? str(o, 'occupier') ?? String(num(o, 'id') ?? num(o, 'occupierId') ?? '?');

// ---------- the nouns: Held and Target data, fixed here ----------

export type OccupierKind = 'holder' | 'event' | 'series' | 'foreign';
type GateOf<T> = T extends `gate:${infer G}` ? G : never;
export type GateKind = GateOf<TargetKind>;

export interface OccupierNoun {
  readonly id: number; readonly name: string; readonly kind: OccupierKind;
  readonly holder?: string | null; readonly rank?: number; readonly pinned?: boolean; readonly keeper?: boolean;
}
export interface RunNoun {
  readonly occupier: string; readonly occupierId: number; readonly kind: OccupierKind; readonly role: Role;
  readonly date: string; readonly hour?: number; readonly key: number; readonly slots: bigint;
}
export interface BitsNoun {
  readonly date: string; readonly hour?: number; readonly key: number; readonly slot: number;
  readonly slots: bigint; readonly run?: bigint; readonly label?: string; readonly ask?: string; readonly width?: number;
}

const noUndefined = (o: Record<string, Json | undefined>): Record<string, Json> => {
  const out: Record<string, Json> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined) out[k] = v;
  return out;
};

export const nouns = {
  occupier: (world: string, o: OccupierNoun): Held => ({
    kind: 'occupier', id: `occupier:${world}:${o.id}`, label: o.name, world,
    data: noUndefined({ id: o.id, name: o.name, kind: o.kind, holder: o.holder ?? null, rank: o.rank ?? 0, pinned: o.pinned ?? false, keeper: o.keeper }),
  }),
  orb: (world: string, o: OccupierNoun): Target => ({
    kind: 'orb', id: `orb:${world}:${o.id}`, world,
    data: noUndefined({ id: o.id, name: o.name, kind: o.kind, holder: o.holder ?? null, label: o.name }),
  }),
  run: (world: string, r: RunNoun): Held => ({
    kind: r.role === 'intent' ? 'softRun' : 'run',
    id: `run:${world}:${r.key}:${r.occupierId}:${r.role}:${hex(r.slots)}`,
    label: `${r.occupier} ${posLabel(where({ date: r.date, ...(r.hour === undefined ? {} : { hour: r.hour }) }))} ${runs(maskSlots(hex(r.slots)))}`,
    world,
    data: noUndefined({ occupier: r.occupier, occupierId: r.occupierId, kind: r.kind, role: r.role, date: r.date, hour: r.hour, key: r.key, slots: hex(r.slots) }),
  }),
  bits: (world: string, b: BitsNoun): Target => ({
    kind: 'bits', id: `bits:${world}:${b.key}:${b.slot}`, world,
    data: noUndefined({
      date: b.date, hour: b.hour, key: b.key, slot: b.slot, slots: hex(b.slots),
      run: b.run === undefined || b.run === 0n ? undefined : hex(b.run), label: b.label, ask: b.ask, width: b.width,
    }),
  }),
  row: (world: string, r: { date: string; hour?: number; key: number; label?: string }): Target => ({
    kind: 'row', id: `row:${world}:${r.key}`, world, data: noUndefined({ date: r.date, hour: r.hour, key: r.key, label: r.label }),
  }),
  field: (world: string, f: { date: string; hour?: number; positions: number }): Target => ({
    kind: 'field', id: `field:${world}`, world, data: noUndefined({ date: f.date, hour: f.hour, positions: f.positions, label: `the ${world} field` }),
  }),
  header: (world: string, seats: number): Target => ({ kind: 'header', id: `header:${world}`, world, data: { seats, label: `${world} header` } }),
  roster: (world: string): Target => ({ kind: 'roster', id: `roster:${world}`, world, data: { label: `the ${world} roster` } }),
  river: (world: string, since: number): Target => ({ kind: 'river', id: `river:${world}`, world, data: { since, label: `the ${world} river` } }),
  inbox: (world: string): Target => ({ kind: 'inbox', id: `inbox:${world}`, world, data: { label: `the ${world} inbox` } }),
  ring: (world: string, global: string, part?: 'port'): Target => ({
    kind: 'ring', id: `ring:${global}${part ? `:${part}` : ''}`, world, data: noUndefined({ global, part, label: part ? `${global} hand port` : global }),
  }),
  rack: (rack: 'relays' | 'antennas', world?: string): Target => ({
    kind: 'rack', id: `rack:${rack}${world ? `:${world}` : ''}`, ...(world ? { world } : {}), data: noUndefined({ rack, label: rack === 'relays' ? `the ${world ?? ''} relay rack` : 'the antenna rack' }),
  }),
  braidTarget: (world: string, relay: string): Target => ({ kind: 'braid', id: `braid:${world}:${relay}`, world, data: { relay, label: `relay ${relay}` } }),
  tick: (world: string, relay: string, t: { date: string; hour?: number; slot: number }): Target => ({
    kind: 'tick', id: `tick:${world}:${relay}:${t.date}:${t.hour ?? ''}:${t.slot}`, world,
    data: noUndefined({ relay, date: t.date, hour: t.hour, slot: t.slot, label: `start ${posLabel(where({ date: t.date, ...(t.hour === undefined ? {} : { hour: t.hour }) }))} slot ${t.slot}` }),
  }),
  gate: (gate: GateKind, data: Record<string, Json> = {}, world?: string): Target => ({
    kind: `gate:${gate}`, id: `gate:${gate}${world ? `:${world}` : ''}`, ...(world ? { world } : {}), data: { label: `${gate} gate`, ...data },
  }),
  tool: (kind: 'lens' | 'needle' | 'tap', data: Record<string, Json> = {}): Held => ({
    kind, id: `tool:${kind}`, label: kind === 'lens' ? 'Lens' : kind === 'needle' ? 'Needle' : 'Tap', data,
  }),
  blank: (kind: 'holder' | 'event' | 'series', f: { name?: string; rank?: number; pinned?: boolean } = {}): Held => ({
    kind: 'blank', id: `blank:${kind}`, label: f.name ? `${kind} ${f.name}` : `a ${kind} blank`, data: noUndefined({ kind, name: f.name, rank: f.rank, pinned: f.pinned }),
  }),
  seat: (tokens = 1): Held => ({ kind: 'seat', id: 'seat', label: `${tokens} seat token${tokens === 1 ? '' : 's'}`, data: { tokens } }),
  spine: (world: string, name: string, legs: readonly { hand: string; length: number; gapAfter?: number; after?: readonly number[] }[]): Held => ({
    kind: 'spine', id: `spine:${world}:${name}`, label: `relay ${name || '(unnamed)'}`, world,
    data: { name, legs: legs.map((l) => noUndefined({ hand: l.hand, length: l.length, gapAfter: l.gapAfter, after: l.after ? [...l.after] : undefined })) },
  }),
  braid: (world: string, relay: string, f: { possible: readonly string[]; standing: string; positions?: number }): Held => ({
    kind: 'braid', id: `braid:${world}:${relay}`, label: `relay ${relay}`, world,
    data: noUndefined({ relay, possible: [...f.possible], standing: f.standing, positions: f.positions }),
  }),
  bead: (world: string, relay: string, leg: number, hand: string): Held => ({
    kind: 'bead', id: `bead:${world}:${relay}:${leg}`, label: `leg ${leg + 1} of ${relay} (${hand})`, world, data: { relay, leg, hand },
  }),
  port: (world: string): Held => ({ kind: 'port', id: `port:${world}`, label: `${world} hand port`, world, data: {} }),
  spark: (world: string, a: {
    token: string; state: string | null; from: string; date: string; hour?: number; key?: number; slots: bigint; stale: 'ok' | 'stale' | 'checking' | 'unverified';
  }): Held => ({
    kind: 'spark', id: `spark:${world}:${a.token}`, label: `ask ${a.token.slice(0, 4)}… from ${a.from}`, world,
    data: noUndefined({ token: a.token, state: a.state, from: a.from, date: a.date, hour: a.hour, key: a.key, slots: hex(a.slots), stale: a.stale }),
  }),
} as const;

// ---------- the verb shape ----------

export interface HandVerb extends Verb {
  /** The §3.2 row this verb answers (0 for the deck's launch verbs). */
  readonly row: number;
  /** What it does in plain words, for a ring that knows only one side. */
  readonly title: string;
  /** The pointer gesture and the keyboard / click path, as §3.2 words them. */
  readonly gesture: string;
  readonly keys: string;
  /** Narrower than the kind: which of this kind it takes (a spark in the right state, the seed tray among trays). */
  heldIs?(h: Held): boolean;
  targetIs?(t: Target): boolean;
  /** Why this pair cannot meet, in words for the dimmed target's tooltip; null when it can. */
  refuse?(h: Held, t: Target, ctx: VerbContext): string | null;
  /** More calls the same drop makes, in order (the MCP lens: initialize, then tools/list). */
  also?(h: Held, t: Target, ctx: VerbContext): readonly Args[];
}

export type Built = Args | { need: readonly Hole[]; partial: Args };
export const isNeed = (b: Built): b is { need: readonly Hole[]; partial: Args } =>
  typeof (b as { need?: unknown }).need === 'object' && Array.isArray((b as { need?: unknown }).need) && 'partial' in b;

const pointerGet = (args: Args, pointer: string): Json | undefined => {
  let at: Json | undefined = args as unknown as Json;
  for (const raw of pointer.split('/').slice(1)) {
    const part = raw.replace(/~1/g, '/').replace(/~0/g, '~');
    if (at === null || typeof at !== 'object' || Array.isArray(at)) return undefined;
    at = (at as Obj)[part];
  }
  return at;
};

/**
 * Args, or what is still missing: every required, visible hole of the
 * operation (and of the verb's variant) that the drop did not fill. An
 * operation the catalogue does not know is passed through; the door answers.
 */
export const complete = (op: string, args: Args, ctx: VerbContext, variant?: string): Built => {
  const operation = ctx.catalogue.byId(op);
  if (!operation) return args;
  const top = (h: Hole): boolean => h.pointer.split('/').length <= 2;
  const holes: Hole[] = operation.variants.length > 0 ? operation.holes.filter(top) : [...operation.holes];
  if (variant) holes.push(...(operation.variants.find((v) => v.key === variant)?.holes ?? []));
  const seen = new Set<string>();
  const need = holes.filter((h) => {
    if (!h.required || h.hidden || h.pointer.includes('*') || seen.has(h.pointer)) return false;
    seen.add(h.pointer);
    const value = pointerGet(args, h.pointer);
    return value === undefined || value === null || value === '';
  });
  return need.length > 0 ? { need, partial: args } : args;
};

// ---------- shared checks ----------

const sameWorld = (h: Held, t: Target): string | null =>
  h.world && t.world && h.world !== t.world ? `${h.label} belongs to ${h.world}, not ${t.world}` : null;

const hasSlots = (hex: string | undefined): boolean => maskOf(hex) > 0n;

// ---------- the curated verbs ----------

type Spec = Omit<HandVerb, 'label' | 'args'> & {
  label(h: Held, t: Target): string;
  build(h: Held, t: Target, ctx: VerbContext, clock: () => number): Args;
};

const cut = (name: string, n = 40): string => (name.length > n ? `${name.slice(0, n - 1)}…` : name);
const tgt = (t: Target): Obj => objectOf(t.data);
const hd = (h: Held): Obj => objectOf(h.data);

/** The soft hold's dial, in minutes, when the orb carries none. */
export const SOFT_MINUTES = 30;

const specs: Spec[] = [
  // --- meta ---
  { id: 'health', op: 'health', row: 1, held: 'lens', target: 'pulse', confirm: null, title: 'Check health',
    gesture: 'Lens ⇢ Pulse', keys: 'Pulse ⏎ › Check health',
    label: () => 'Check health', build: () => ({}) },
  { id: 'presets', op: 'presets', row: 2, held: 'lens', target: 'tray', confirm: null, title: 'Read presets',
    gesture: 'Lens ⇢ Forge seed tray', keys: 'tray ⏎ › Read presets',
    targetIs: (t) => (str(tgt(t), 'tray') ?? 'seeds') === 'seeds',
    label: () => 'Read presets', build: () => ({}) },
  { id: 'openapi', op: 'openapi', row: 3, held: 'lens', target: 'deck', confirm: null, title: 'Reload spec',
    gesture: 'Lens ⇢ Deck spine', keys: 'Deck ⏎ › Reload spec; ⌘K reload spec',
    label: () => 'Reload spec', build: () => ({}) },
  // Only the docs card: every other card's one place is its own launch, so a keyboard lift never aims at the portal.
  { id: 'docs', op: 'docs', row: 4, held: 'card', target: 'gate:docs', confirm: null, title: 'Open the docs',
    gesture: 'Deck spine ⇢ Docs portal', keys: 'portal ⏎',
    heldIs: (h) => hd(h).spine === true || str(hd(h), 'op') === 'docs',
    label: () => 'Open the docs', build: () => ({}) },
  { id: 'mcp-initialize', op: 'mcp', variant: 'initialize', row: 5, held: 'lens', target: 'gate:mcp', confirm: null,
    title: 'List the MCP tools', gesture: 'Lens ⇢ MCP lens (initialize + tools/list)', keys: 'M opens the lens, ⏎ › List tools',
    label: () => 'List the MCP tools',
    build: () => ({ body: { jsonrpc: '2.0', id: 1, method: 'initialize', params: { protocolVersion: '2025-06-18', capabilities: {}, clientInfo: { name: 'schedule-bit-playground', version: '1' } } } }),
    also: () => [{ body: { jsonrpc: '2.0', id: 2, method: 'tools/list' } }] },
  { id: 'mcp-call', op: 'mcp', variant: 'tools/call', row: 5, held: 'toolCard', target: 'gate:mcp', confirm: null,
    title: 'Call this tool over MCP', gesture: 'Tool card ⇢ MCP gate', keys: '⎵ tool → gate ⏎',
    // A tool that still needs its arguments is not sent empty: the MCP lens opens its card, holes and all, instead.
    heldIs: (h) => hd(h).ready !== false,
    label: (h) => `Call ${str(hd(h), 'tool') ?? 'the tool'} over MCP`,
    build: (h) => ({ body: { jsonrpc: '2.0', id: 3, method: 'tools/call', params: { name: str(hd(h), 'tool') ?? '', arguments: objectOf(hd(h).args) } } }) },
  { id: 'whoami', op: 'whoami', row: 6, held: 'lens', target: 'persona', confirm: null, title: 'Who is this key',
    gesture: 'Lens ⇢ persona chip', keys: 'chip ⏎ › Who is this key',
    label: (_h, t) => `Who is key ${str(tgt(t), 'persona') ?? 'A'}`, build: () => ({}) },
  { id: 'operator-mint', op: 'operatorIssueKey', row: 7, held: 'capsule', target: 'gate:mint', confirm: null,
    title: 'Mint a workspace key', gesture: 'workspace capsule ⇢ Mint (operator drawer)', keys: '⎵ capsule → Mint ⏎',
    heldIs: (h) => str(hd(h), 'capsule') === 'workspace',
    label: (h) => `Mint the first key of ${str(hd(h), 'workspace') ?? 'a workspace'}`,
    build: (h) => {
      const o = hd(h);
      return { body: noUndefined({ workspace: str(o, 'workspace'), scopes: strs(o, 'scopes'), plan: str(o, 'plan'), expiresInDays: num(o, 'expiresInDays'), label: str(o, 'label') }) };
    } },
  { id: 'signup-config', op: 'signupConfig', row: 8, held: 'lens', target: 'gate:signup', confirm: null,
    title: 'Check sign-up', gesture: 'Lens ⇢ SIGN UP gate', keys: 'gate ⏎ › Check',
    label: () => 'Check whether sign-up is available', build: () => ({}) },
  { id: 'signup', op: 'signup', row: 9, held: 'capsule', target: 'gate:signup', confirm: null,
    title: 'Sign up', gesture: 'Email capsule ⇢ SIGN UP gate (opens /signup)', keys: '⎵ capsule → gate ⏎',
    heldIs: (h) => str(hd(h), 'capsule') === 'email',
    label: (h) => `Sign up ${str(hd(h), 'workspace') ?? 'a workspace'}`,
    // The Turnstile token exists only on /signup: this drop always answers `need`, and /signup finishes it.
    build: (h) => ({ body: noUndefined({ email: str(hd(h), 'email'), workspace: str(hd(h), 'workspace') }) }) },
  { id: 'verify', op: 'signupVerify', row: 10, held: 'link', target: 'gate:verify', confirm: null,
    title: 'Use the mailed link', gesture: 'mailed link (text/uri-list) ⇢ VERIFY socket', keys: 'socket: paste the link, ⏎',
    label: () => 'Verify the sign-up', build: (h) => ({ body: noUndefined({ token: str(hd(h), 'token') }) }) },

  // --- Worlds ---
  { id: 'list-worlds', op: 'listWorlds', row: 11, held: 'lens', target: 'ground', confirm: null, title: 'Re-read Worlds',
    gesture: 'Lens ⇢ empty stage ground', keys: 'ground ⏎ › Re-read Worlds',
    label: () => 'Re-read Worlds', build: () => ({}) },
  { id: 'create-world', op: 'createWorld', row: 12, held: 'seed', target: 'ground', confirm: null, title: 'Make a World',
    gesture: 'Seed ⇢ ground', keys: 'N, or ⎵ seed → ground ⏎',
    label: (h) => `Make ${str(hd(h), 'name') ? `World ${str(hd(h), 'name')}` : 'a World'} (${str(hd(h), 'preset') ?? 'temporal-hour'})`,
    build: (h) => {
      const o = hd(h);
      return { body: noUndefined({ name: str(o, 'name'), preset: str(o, 'preset') ?? 'temporal-hour', allowDisplace: bool(o, 'allowDisplace'), tieBreak: str(o, 'tieBreak') }) };
    } },
  { id: 'get-world', op: 'getWorld', row: 13, held: 'lens', target: 'ring', confirm: null, title: 'Read this World',
    gesture: 'Lens ⇢ World ring', keys: 'ring ⏎ › Read',
    targetIs: (t) => str(tgt(t), 'part') === undefined,
    label: (_h, t) => `Read ${t.world ?? 'the World'}`, build: (_h, t) => noUndefined({ w: t.world }) },
  { id: 'list-occupiers', op: 'listOccupiers', row: 14, held: 'lens', target: 'roster', confirm: null, title: 'Re-read the roster',
    gesture: 'Lens ⇢ Roster rail', keys: 'Roster ⏎ › Re-read',
    label: (_h, t) => `Re-read who may hold time in ${t.world ?? 'this World'}`, build: (_h, t) => noUndefined({ w: t.world }) },
  { id: 'declare', op: 'declareOccupier', row: 15, held: 'blank', target: 'roster', confirm: null, title: 'Declare an occupier',
    gesture: 'Blank (holder, event, series) ⇢ Roster', keys: '+ on the Roster, or ⎵ blank → roster ⏎, then the name ⏎',
    refuse: (h) => (str(hd(h), 'kind') ?? 'holder') !== 'holder' ? `an ${str(hd(h), 'kind')} is declared in a holder's name: drop it on a holder orb` : null,
    label: (h, t) => `Declare ${str(hd(h), 'kind') ?? 'holder'} ${str(hd(h), 'name') ?? '(name it)'} in ${t.world ?? 'this World'}`,
    build: (h, t) => {
      const o = hd(h);
      return { w: t.world ?? null, body: noUndefined({ name: str(o, 'name'), kind: str(o, 'kind') ?? 'holder', rank: num(o, 'rank'), pinned: bool(o, 'pinned') }) };
    } },
  { id: 'declare-under', op: 'declareOccupier', row: 15, held: 'blank', target: 'orb', confirm: null, title: 'Declare it in this holder\'s name',
    gesture: 'event or series blank ⇢ a holder orb (binds holder)', keys: '⎵ blank → orb ⏎, then the name ⏎',
    heldIs: (h) => ['event', 'series'].includes(str(hd(h), 'kind') ?? ''),
    targetIs: (t) => str(tgt(t), 'kind') === 'holder',
    label: (h, t) => `Declare ${str(hd(h), 'kind')} ${str(hd(h), 'name') ?? '(name it)'} in ${str(tgt(t), 'name') ?? 'the holder'}'s name`,
    build: (h, t) => {
      const o = hd(h);
      return { w: t.world ?? null, body: noUndefined({ name: str(o, 'name'), kind: str(o, 'kind'), holder: str(tgt(t), 'name'), rank: num(o, 'rank'), pinned: bool(o, 'pinned') }) };
    } },
  { id: 'set-capacity', op: 'setCapacity', row: 16, held: 'seat', target: 'header', confirm: null, title: 'Add seats',
    gesture: 'Seat token ⇢ World header (one token = one more seat)', keys: 'header S, ↑, ⏎ (↓ is disabled: capacity only grows)',
    refuse: (h) => ((num(hd(h), 'tokens') ?? 1) < 1 ? 'capacity only grows' : null),
    label: (h, t) => `Grow ${t.world ?? 'the World'} to ${(num(tgt(t), 'seats') ?? 1) + (num(hd(h), 'tokens') ?? 1)} seats`,
    build: (h, t) => ({ w: t.world ?? null, body: { seats: (num(tgt(t), 'seats') ?? 1) + (num(hd(h), 'tokens') ?? 1) } }) },

  // --- the door ---
  { id: 'claim', op: 'claim', row: 17, held: 'occupier', target: 'bits', confirm: null, title: 'Claim these slots',
    gesture: 'Occupier orb ⇢ the field, brushing across bits', keys: '⎵ orb → arrows to a slot, ⇧→ extends, ⏎; or select bits then C',
    refuse: (h, t) => sameWorld(h, t) ?? (hasSlots(str(tgt(t), 'slots')) ? null : 'no slots here'),
    label: (h, t) => `Claim ${words(posLabel(tgt(t)), slotsLabel(str(tgt(t), 'slots')))} for ${cut(nameOf(hd(h)))}`,
    build: (h, t) => ({ w: t.world ?? null, body: { who: whoOf(hd(h)), ...where(tgt(t)), slots: str(tgt(t), 'slots') ?? '0x0' } }) },
  { id: 'claim-soft', op: 'claim', variant: 'soft', row: 17, held: 'occupier', target: 'bits', mods: ['soft'], confirm: null,
    title: 'Hold these slots softly', gesture: 'Occupier orb ⇢ the field with S held (until dial)', keys: '⎵ orb → slot, S, ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? (hasSlots(str(tgt(t), 'slots')) ? null : 'no slots here'),
    preview: () => 'ok',
    label: (h, t) => `Hold ${words(posLabel(tgt(t)), slotsLabel(str(tgt(t), 'slots')))} softly for ${cut(nameOf(hd(h)))} (a soft hold reserves nothing)`,
    build: (h, t, _ctx, clock) => ({
      w: t.world ?? null,
      body: { who: whoOf(hd(h)), ...where(tgt(t)), slots: str(tgt(t), 'slots') ?? '0x0', soft: true, until: clock() + (num(hd(h), 'softMinutes') ?? SOFT_MINUTES) * 60_000 },
    }) },
  { id: 'explain', op: 'explainClaim', row: 18, held: 'occupier', target: 'bits', mods: ['explain'], confirm: null,
    title: 'Ask the door, light nothing', gesture: 'the claim drag with E held', keys: 'E instead of ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? (hasSlots(str(tgt(t), 'slots')) ? null : 'no slots here'),
    label: (h, t) => `Ask the door about ${words(posLabel(tgt(t)), slotsLabel(str(tgt(t), 'slots')))} for ${cut(nameOf(hd(h)))}`,
    build: (h, t) => ({ w: t.world ?? null, body: { who: whoOf(hd(h)), ...where(tgt(t)), slots: str(tgt(t), 'slots') ?? '0x0' } }) },
  { id: 'explain-door', op: 'explainClaim', row: 18, held: 'occupier', target: 'gate:door', confirm: null,
    title: 'Ask the door about the selection', gesture: 'Occupier orb ⇢ DOOR (the selected bits)', keys: '⎵ orb → Door ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? (hasSlots(str(tgt(t), 'slots')) ? null : 'select some bits first'),
    label: (h, t) => `Ask the door about ${words(posLabel(tgt(t)), slotsLabel(str(tgt(t), 'slots')))} for ${cut(nameOf(hd(h)))}`,
    build: (h, t) => ({ w: t.world ?? null, body: { who: whoOf(hd(h)), ...where(tgt(t)), slots: str(tgt(t), 'slots') ?? '0x0' } }) },
  { id: 'release', op: 'release', row: 19, held: 'run', target: 'gate:void', confirm: null, title: 'Release this run',
    gesture: 'held run ⇢ Void (or fling it off the field)', keys: 'select the run then Delete, or ⎵ run → Void ⏎',
    refuse: sameWorld,
    label: (h) => `Release ${cut(nameOf(hd(h)))}'s ${words(posLabel(hd(h)), slotsLabel(str(hd(h), 'slots')))}`,
    build: (h) => ({ w: h.world ?? null, body: { who: whoOf(hd(h)), ...where(hd(h)), slots: str(hd(h), 'slots') ?? '0x0' } }) },
  { id: 'handover', op: 'handover', row: 20, held: 'run', target: 'orb', confirm: null, title: 'Hand this run over',
    gesture: 'held run ⇢ another occupier orb', keys: 'H → pick the orb, or ⎵ run → orb ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? (num(tgt(t), 'id') === num(hd(h), 'occupierId') ? 'it is already theirs' : str(tgt(t), 'kind') === 'foreign' ? 'a copy of another World cannot hold time here' : null),
    label: (h, t) => `Hand ${words(posLabel(hd(h)), slotsLabel(str(hd(h), 'slots')))} from ${cut(nameOf(hd(h)))} to ${cut(str(tgt(t), 'name') ?? '?')}`,
    build: (h, t) => ({ w: h.world ?? null, body: { from: whoOf(hd(h)), to: whoOf(tgt(t)), ...where(hd(h)), slots: str(hd(h), 'slots') ?? '0x0' } }) },
  { id: 'confirm', op: 'confirm', row: 21, held: 'softRun', target: 'gate:seal', confirm: null, title: 'Make it firm',
    gesture: 'soft run ⇢ SEAL (or double-click it)', keys: 'F, or ⎵ run → Seal ⏎',
    refuse: sameWorld,
    label: (h) => `Make ${cut(nameOf(hd(h)))}'s soft ${words(posLabel(hd(h)), slotsLabel(str(hd(h), 'slots')))} firm`,
    build: (h) => ({ w: h.world ?? null, body: { who: whoOf(hd(h)), ...where(hd(h)), slots: str(hd(h), 'slots') ?? '0x0' } }) },
  { id: 'give-up', op: 'giveUp', row: 22, held: 'softRun', target: 'gate:void', confirm: null, title: 'Let the soft hold go',
    gesture: 'soft run ⇢ Void', keys: 'G, or ⎵ run → Void ⏎',
    refuse: sameWorld,
    label: (h) => `Let ${cut(nameOf(hd(h)))}'s soft hold on ${words(posLabel(hd(h)), slotsLabel(str(hd(h), 'slots')))} go`,
    build: (h) => ({ w: h.world ?? null, body: { who: whoOf(hd(h)), ...where(hd(h)), slots: str(hd(h), 'slots') ?? '0x0' } }) },
  { id: 'queue', op: 'queue', row: 23, held: 'occupier', target: 'bits', mods: ['queue'], confirm: null, title: 'Wait in line for these slots',
    gesture: 'Orb ⇢ someone else\'s held run with Q', keys: 'Q → pick the orb',
    refuse: (h, t) => sameWorld(h, t) ?? (hasSlots(str(tgt(t), 'run') ?? str(tgt(t), 'slots')) ? null : 'nothing to queue for here'),
    label: (h, t) => `Queue ${cut(nameOf(hd(h)))} for ${words(posLabel(tgt(t)), slotsLabel(str(tgt(t), 'run') ?? str(tgt(t), 'slots')))}`,
    build: (h, t) => ({ w: t.world ?? null, body: { who: whoOf(hd(h)), ...where(tgt(t)), slots: str(tgt(t), 'run') ?? str(tgt(t), 'slots') ?? '0x0' } }) },
  { id: 'queue-lip', op: 'queue', row: 23, held: 'occupier', target: 'gate:queue', confirm: null, title: 'Wait in line (queue lip)',
    gesture: 'Orb ⇢ the run\'s queue lip', keys: '⎵ orb → Queue ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? (hasSlots(str(tgt(t), 'slots')) ? null : 'pick a held run first'),
    label: (h, t) => `Queue ${cut(nameOf(hd(h)))} for ${words(posLabel(tgt(t)), slotsLabel(str(tgt(t), 'slots')))}`,
    build: (h, t) => ({ w: t.world ?? null, body: { who: whoOf(hd(h)), ...where(tgt(t)), slots: str(tgt(t), 'slots') ?? '0x0' } }) },
  { id: 'sweep', op: 'sweep', row: 24, held: 'needle', target: 'field', confirm: null, title: 'Sweep lapsed soft holds',
    gesture: 'Needle ⇢ drag across the field', keys: '⇧S on the field, or ⎵ needle → field ⏎',
    label: (_h, t) => `Sweep lapsed soft holds in ${t.world ?? 'this World'}`, build: (_h, t) => ({ w: t.world ?? null, body: {} }) },

  // --- reading ---
  { id: 'offered', op: 'offered', row: 25, held: 'lens', target: 'row', confirm: null, title: 'Read what is offered',
    gesture: 'Lens ⇢ row header', keys: 'row O',
    label: (_h, t) => `Read what is offered ${posLabel(tgt(t))}`, build: (_h, t) => ({ w: t.world ?? null, ...where(tgt(t)) }) },
  { id: 'holdings', op: 'holdings', row: 26, held: 'occupier', target: 'row', confirm: null, title: 'Read what they hold here',
    gesture: 'Orb ⇢ the row header\'s lens socket', keys: '⎵ orb → row ⏎',
    refuse: sameWorld,
    label: (h, t) => `Read what ${cut(nameOf(hd(h)))} holds ${posLabel(tgt(t))}`,
    // The query form resolves names only, so `who` is always the name here.
    build: (h, t) => ({ w: t.world ?? null, who: str(hd(h), 'name') ?? str(hd(h), 'occupier') ?? null, ...where(tgt(t)) }) },
  { id: 'journal', op: 'journal', row: 27, held: 'lens', target: 'river', confirm: null, title: 'Page in older commits',
    gesture: 'Lens ⇢ River', keys: 'River J; PageUp older; ←/→ scrub; End live',
    label: (_h, t) => `Read the journal of ${t.world ?? 'this World'} from #${(num(tgt(t), 'since') ?? 0) + 1}`,
    build: (_h, t) => ({ w: t.world ?? null, since: Math.max(0, num(tgt(t), 'since') ?? 0), limit: 100 }) },
  { id: 'planes', op: 'planes', row: 28, held: 'lens', target: 'field', confirm: null, title: 'Check the field against the server',
    gesture: 'Lens ⇢ field (truth check)', keys: 'field ⇧R',
    label: (_h, t) => `Check ${t.world ?? 'the field'} against the server`,
    build: (_h, t) => ({ w: t.world ?? null, ...where(tgt(t)), positions: Math.min(31, Math.max(1, num(tgt(t), 'positions') ?? 7)) }) },

  // --- watching ---
  { id: 'watch-ticket', op: 'watchTicket', row: 29, held: 'tap', target: 'ring', confirm: null, title: 'Tap this World',
    gesture: 'Tap ⇢ World ring (then the socket opens with the ticket)', keys: '⎵ tap → ring ⏎',
    targetIs: (t) => str(tgt(t), 'part') === undefined,
    label: (_h, t) => `Tap ${t.world ?? 'the World'} for a watch ticket`,
    build: (h, t) => {
      // The ticket names the viewer (§3.2 row 29: {name, color} from YOU) when the Tap carries them.
      const body = noUndefined({ name: str(hd(h), 'name'), color: str(hd(h), 'color') });
      return { w: t.world ?? null, ...(Object.keys(body).length > 0 ? { body } : {}) };
    } },

  // --- relays: the planner's side ---
  { id: 'list-hands', op: 'listHands', row: 31, held: 'lens', target: 'ring', confirm: null, title: 'Read the hands taken in',
    gesture: 'Lens ⇢ the planner\'s hand port', keys: 'port ⏎',
    targetIs: (t) => str(tgt(t), 'part') === 'port',
    label: (_h, t) => `Read the hands ${t.world ?? 'this planner'} took in`, build: (_h, t) => ({ w: t.world ?? null }) },
  { id: 'take-in', op: 'takeIn', row: 32, held: 'port', target: 'ring', confirm: null, title: 'Take this hand in',
    gesture: 'Port ⇢ another World ring (again refreshes the copy)', keys: 'T on a planner → pick a hand, ⏎',
    targetIs: (t) => str(tgt(t), 'part') === undefined,
    refuse: (h, t) => {
      if (h.world && t.world === h.world) return 'a World cannot take itself in';
      // Hands share the planner's positions: a minute World and an hour World cannot be each other's hands.
      const a = str(hd(h), 'preset');
      const b = str(tgt(t), 'preset');
      return a && b && a !== b ? `${t.world ?? 'that World'} is a ${b.replace('temporal-', '')} World; ${h.world ?? 'the planner'} is a ${a.replace('temporal-', '')} World` : null;
    },
    label: (h, t) => `Take ${str(tgt(t), 'global') ?? t.world ?? 'the hand'} into ${h.world ?? 'the planner'}`,
    build: (h, t) => ({ w: h.world ?? null, body: { hand: str(tgt(t), 'global') ?? t.world ?? '' } }) },
  { id: 'list-relays', op: 'listRelays', row: 33, held: 'lens', target: 'rack', confirm: null, title: 'Read the relays planned here',
    gesture: 'Lens ⇢ the planner\'s relay rack', keys: 'rack ⏎',
    targetIs: (t) => str(tgt(t), 'rack') === 'relays',
    label: (_h, t) => `Read the relays of ${t.world ?? 'this planner'}`, build: (_h, t) => ({ w: t.world ?? null }) },
  { id: 'lay-relay', op: 'layRelay', row: 34, held: 'spine', target: 'gate:lay', confirm: null, title: 'Lay this relay',
    gesture: 'Spine ⇢ LAY (hand ring ⇢ spine adds a leg in the Loom)', keys: 'In the Loom: Insert adds a leg, +/− length, [ ] gap, A after, ⏎ lays',
    refuse: (h, t) => sameWorld(h, t) ?? (Array.isArray(hd(h).legs) && (hd(h).legs as Json[]).length > 0 ? null : 'add a leg first'),
    label: (h) => `Lay relay ${str(hd(h), 'name') || '(name it)'} with ${((hd(h).legs as Json[] | undefined) ?? []).length} legs`,
    build: (h, t) => {
      const legs = Array.isArray(hd(h).legs) ? (hd(h).legs as Json[]) : [];
      return { w: h.world ?? t.world ?? null, body: noUndefined({ name: str(hd(h), 'name') || undefined, legs }) };
    } },
  { id: 'get-relay', op: 'getRelay', row: 35, held: 'lens', target: 'braid', confirm: null, title: 'Read this relay',
    gesture: 'Lens ⇢ braid', keys: 'braid ⏎ › Read',
    label: (_h, t) => `Read relay ${str(tgt(t), 'relay') ?? ''}`, build: (_h, t) => ({ w: t.world ?? null, r: str(tgt(t), 'relay') ?? '' }) },
  { id: 'relay-windows', op: 'relayWindows', row: 36, held: 'braid', target: 'field', confirm: null, title: 'Read where the relay fits',
    gesture: 'Lift the braid over the planner field (one read per lift)', keys: '⎵ on a braid announces "N starts"; ticks become targets',
    refuse: sameWorld,
    label: (h, t) => `Read where relay ${str(hd(h), 'relay') ?? ''} fits from ${posLabel(tgt(t))}`,
    build: (h, t) => ({ w: h.world ?? null, r: str(hd(h), 'relay') ?? '', ...where(tgt(t)), positions: Math.min(512, Math.max(1, num(hd(h), 'positions') ?? num(tgt(t), 'positions') ?? 1)) }) },
  { id: 'relay-ask', op: 'relayAct', variant: 'ask', row: 37, held: 'braid', target: 'tick', confirm: null, title: 'Ask every hand from this start',
    gesture: 'braid ⇢ lit tick', keys: 'braid ⎵ → tick ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? (str(tgt(t), 'relay') !== str(hd(h), 'relay') ? 'a tick of another relay' : possibleRefusal(h, 'ask')),
    label: (h, t) => `Ask for relay ${str(hd(h), 'relay') ?? ''} from ${posLabel(tgt(t))} slot ${num(tgt(t), 'slot') ?? '?'}`,
    build: (h, t) => ({ w: h.world ?? null, r: str(hd(h), 'relay') ?? '', body: { act: 'ask', start: { ...where(tgt(t)), slot: num(tgt(t), 'slot') ?? 0 } } }) },
  { id: 'relay-sync', op: 'relayAct', variant: 'sync', row: 37, held: 'braid', target: 'gate:sync', confirm: null, title: 'Sync the answers',
    gesture: 'tug the knot, or braid ⇢ SYNC', keys: 'Y',
    refuse: (h, t) => sameWorld(h, t) ?? possibleRefusal(h, 'sync'),
    label: (h) => `Sync relay ${str(hd(h), 'relay') ?? ''}`,
    build: (h) => ({ w: h.world ?? null, r: str(hd(h), 'relay') ?? '', body: { act: 'sync' } }) },
  { id: 'relay-withdraw', op: 'relayAct', variant: 'withdraw', row: 37, held: 'braid', target: 'gate:void', confirm: null, title: 'Withdraw every ask',
    gesture: 'braid ⇢ Void', keys: '⎵ braid → Void ⏎',
    refuse: (h, t) => sameWorld(h, t) ?? possibleRefusal(h, 'withdraw'),
    label: (h) => `Withdraw relay ${str(hd(h), 'relay') ?? ''}`,
    build: (h) => ({ w: h.world ?? null, r: str(hd(h), 'relay') ?? '', body: { act: 'withdraw' } }) },
  { id: 'relay-delegate', op: 'relayAct', variant: 'delegate', row: 37, held: 'bead', target: 'ring', confirm: null, title: 'Delegate this leg',
    gesture: 'leg bead ⇢ a taken-in hand ring', keys: 'bead ⎵ → ring ⏎',
    targetIs: (t) => str(tgt(t), 'part') === undefined,
    refuse: (h, t, ctx) => {
      const to = str(tgt(t), 'global') ?? t.world;
      if (to === str(hd(h), 'hand')) return 'that hand already has this leg';
      const relays = objectOf(h.world ? ctx.records.get(h.world, 'relays') : undefined);
      const one = (Array.isArray(relays.relays) ? relays.relays : []).map(objectOf).find((r) => str(r, 'name') === str(hd(h), 'relay'));
      const possible = one ? strs(one, 'possible') : undefined;
      return possible && !possible.includes('delegate') ? `possible acts: ${possible.join(', ') || 'none'}` : null;
    },
    preview: (h, t, ctx) => {
      const hands = objectOf(h.world ? ctx.records.get(h.world, 'hands') : undefined);
      const list = Array.isArray(hands.hands) ? hands.hands.map(objectOf) : null;
      if (!list) return 'maybe';
      const to = str(tgt(t), 'global') ?? t.world;
      return list.some((x) => str(x, 'hand') === to) ? 'ok' : 'no';
    },
    label: (h, t) => `Delegate leg ${(num(hd(h), 'leg') ?? 0) + 1} of ${str(hd(h), 'relay') ?? ''} to ${str(tgt(t), 'global') ?? t.world ?? '?'}`,
    build: (h, t) => ({ w: h.world ?? null, r: str(hd(h), 'relay') ?? '', body: { act: 'delegate', leg: num(hd(h), 'leg') ?? 0, to: str(tgt(t), 'global') ?? t.world ?? '' } }) },

  // --- asks: the hand's side ---
  { id: 'list-asks', op: 'listAsks', row: 38, held: 'lens', target: 'inbox', confirm: null, title: 'Read the inbox',
    gesture: 'Lens ⇢ Inbox tray', keys: 'tray ⏎',
    label: (_h, t) => `Read the asks made of ${t.world ?? 'this hand'}`, build: (_h, t) => ({ w: t.world ?? null }) },
  { id: 'answer-accept', op: 'answerAsk', variant: 'accept', row: 39, held: 'spark', target: 'gate:accept', confirm: null, title: 'Accept this ask',
    gesture: 'spark ⇢ ACCEPT', keys: 'A',
    refuse: (h, t) => sameWorld(h, t) ?? askGate(h, 'accept'),
    label: (h) => `Accept ask ${str(hd(h), 'token')?.slice(0, 4) ?? ''}… from ${str(hd(h), 'from') ?? '?'}`,
    build: (h) => ({ w: h.world ?? null, token: str(hd(h), 'token') ?? '', body: { verdict: 'accept' } }) },
  { id: 'answer-move', op: 'answerAsk', variant: 'moveTo', row: 39, held: 'spark', target: 'bits', confirm: null, title: 'Move it along the row',
    gesture: 'slide the spark or run along its row', keys: 'M + arrows, then ⏎',
    refuse: (h, t, ctx) => sameWorld(h, t) ?? samePosition(h, t) ?? moveRefusal(h, t, ctx) ?? askGate(h, 'moveTo'),
    label: (h, t) => `Move ask ${str(hd(h), 'token')?.slice(0, 4) ?? ''}… to ${slotsLabel(movedHex(h, t))}`,
    build: (h, t) => ({ w: h.world ?? null, token: str(hd(h), 'token') ?? '', body: { verdict: 'moveTo', slots: movedHex(h, t) } }) },

  { id: 'answer-accept-down', op: 'answerAsk', variant: 'accept', row: 39, held: 'spark', target: 'bits', confirm: null, title: 'Accept it where it asks',
    gesture: 'spark ⇢ down into its slots', keys: 'A',
    refuse: (h, t) => sameWorld(h, t) ?? samePosition(h, t) ?? (shiftOf(h, t) === 0 ? askGate(h, 'accept') : 'not its slots: slide to move it'),
    label: (h) => `Accept ask ${str(hd(h), 'token')?.slice(0, 4) ?? ''}… where it asks`,
    build: (h) => ({ w: h.world ?? null, token: str(hd(h), 'token') ?? '', body: { verdict: 'accept' } }) },
  { id: 'answer-reject', op: 'answerAsk', variant: 'reject', row: 39, held: 'spark', target: 'gate:reject', confirm: null, title: 'Reject this ask',
    gesture: 'spark ⇢ REJECT (it shatters)', keys: 'R',
    refuse: (h, t) => sameWorld(h, t) ?? askGate(h, 'reject'),
    label: (h) => `Reject ask ${str(hd(h), 'token')?.slice(0, 4) ?? ''}… from ${str(hd(h), 'from') ?? '?'}`,
    build: (h) => ({ w: h.world ?? null, token: str(hd(h), 'token') ?? '', body: { verdict: 'reject' } }) },
  { id: 'answer-take-back', op: 'answerAsk', variant: 'takeBack', row: 39, held: 'spark', target: 'gate:return', confirm: null, title: 'Take the yes back',
    gesture: 'accepted run ⇢ RETURN', keys: 'U',
    refuse: (h, t) => sameWorld(h, t) ?? askGate(h, 'takeBack'),
    label: (h) => `Take back the yes to ${str(hd(h), 'token')?.slice(0, 4) ?? ''}…`,
    build: (h) => ({ w: h.world ?? null, token: str(hd(h), 'token') ?? '', body: { verdict: 'takeBack' } }) },
  // --- grants ---
  { id: 'list-grants', op: 'listGrants', row: 40, held: 'lens', target: 'band', confirm: null, title: 'Read the grants',
    gesture: 'Lens ⇢ Bridges band', keys: 'band ⏎', label: () => 'Read the grants given and received', build: () => ({}) },
  { id: 'give-grant', op: 'giveGrant', row: 41, held: 'bridge', target: 'ring', confirm: 'destructive', title: 'Let another workspace see this World',
    gesture: 'Bridge ⇢ World ring; type the recipient; see/ask chips', keys: '⎵ bridge → ring ⏎, type, ⏎',
    heldIs: (h) => str(hd(h), 'id') === undefined,
    targetIs: (t) => str(tgt(t), 'part') === undefined,
    refuse: (h) => canRefusal(h),
    label: (h, t) => `Let ${str(hd(h), 'to') ?? '(type who)'} ${(strs(hd(h), 'can') ?? ['see']).join(' and ')} ${t.world ?? 'this World'}`,
    build: (h, t) => ({ body: noUndefined({ to: str(hd(h), 'to'), world: t.world ?? '*', can: strs(hd(h), 'can') ?? ['see'] }) }) },
  { id: 'give-grant-all', op: 'giveGrant', row: 41, held: 'bridge', target: 'sigil', confirm: 'destructive', title: 'Let another workspace see every World',
    gesture: 'Bridge ⇢ the workspace sigil (world *)', keys: '⎵ bridge → sigil ⏎, type, ⏎',
    heldIs: (h) => str(hd(h), 'id') === undefined,
    refuse: (h) => canRefusal(h),
    label: (h) => `Let ${str(hd(h), 'to') ?? '(type who)'} ${(strs(hd(h), 'can') ?? ['see']).join(' and ')} every World`,
    build: (h) => ({ body: noUndefined({ to: str(hd(h), 'to'), world: '*', can: strs(hd(h), 'can') ?? ['see'] }) }) },
  { id: 'revoke-grant', op: 'revokeGrant', row: 42, held: 'bridge', target: 'gate:void', confirm: 'destructive', title: 'Revoke this grant',
    gesture: 'Bridge arc ⇢ Void (confirms)', keys: '⎵ arc → Void ⏎',
    heldIs: (h) => str(hd(h), 'id') !== undefined,
    label: (h) => `Revoke grant ${str(hd(h), 'id') ?? ''}`, build: (h) => ({ id: str(hd(h), 'id') ?? '' }) },

  // --- keys ---
  { id: 'list-keys', op: 'listKeys', row: 43, held: 'lens', target: 'vault', confirm: null, title: 'Read the keys',
    gesture: 'Lens ⇢ Vault', keys: 'Vault ⏎', label: () => 'Read every key (never a secret)', build: () => ({}) },
  { id: 'create-key', op: 'createKey', row: 44, held: 'blankKey', target: 'vault', confirm: null, title: 'Mint a key',
    gesture: 'Blank key ⇢ Vault; scope chips first; expiry dial', keys: '⎵ key blank → Vault ⏎',
    label: (h) => `Mint a key with ${(strs(hd(h), 'scopes') ?? ['(pick scopes)']).join(', ')}`,
    build: (h) => ({ body: noUndefined({ scopes: strs(hd(h), 'scopes'), label: str(hd(h), 'label'), expiresInDays: num(hd(h), 'expiresInDays') }) }) },
  { id: 'revoke-key', op: 'revokeKey', row: 45, held: 'key', target: 'gate:void', confirm: 'destructive', title: 'Revoke this key',
    gesture: 'Key card ⇢ Void (confirms)', keys: '⎵ key → Void ⏎',
    label: (h) => (bool(hd(h), 'own') ? 'Revoke this key: this ends your session' : `Revoke key ${str(hd(h), 'id') ?? ''}`),
    build: (h) => ({ id: str(hd(h), 'id') ?? '' }) },
  { id: 'rotate-key', op: 'rotateKey', row: 46, held: 'key', target: 'gate:turbine', confirm: 'destructive', title: 'Rotate this key',
    gesture: 'Key card ⇢ Turbine (grace dial; confirms)', keys: '⎵ key → Turbine ⏎',
    label: (h) => (bool(hd(h), 'own') ? 'Rotate this key: the session moves to its successor' : `Rotate key ${str(hd(h), 'id') ?? ''}`),
    build: (h) => ({ id: str(hd(h), 'id') ?? '', body: noUndefined({ graceSeconds: num(hd(h), 'graceSeconds'), expiresInDays: num(hd(h), 'expiresInDays') }) }) },

  // --- webhooks ---
  { id: 'list-webhooks', op: 'listWebhooks', row: 47, held: 'lens', target: 'rack', confirm: null, title: 'Read the antennas',
    gesture: 'Lens ⇢ Antenna rack', keys: 'rack ⏎',
    targetIs: (t) => str(tgt(t), 'rack') === 'antennas',
    label: () => 'Read where events are posted', build: () => ({}) },
  { id: 'create-webhook', op: 'createWebhook', row: 48, held: 'antenna', target: 'rack', confirm: 'destructive', title: 'Post events to a URL',
    gesture: 'Antenna ⇢ rack; type the https URL; event chips (confirms)', keys: '⎵ antenna → rack ⏎',
    heldIs: (h) => str(hd(h), 'id') === undefined,
    targetIs: (t) => str(tgt(t), 'rack') === 'antennas',
    label: (h) => `Post ${(strs(hd(h), 'events') ?? ['*']).join(', ')} events to ${str(hd(h), 'url') ?? '(type a URL)'}`,
    build: (h) => ({ body: noUndefined({ url: str(hd(h), 'url'), events: strs(hd(h), 'events') }) }) },
  { id: 'delete-webhook', op: 'deleteWebhook', row: 49, held: 'antenna', target: 'gate:void', confirm: 'destructive', title: 'Stop posting there',
    gesture: 'Antenna ⇢ Void (confirms)', keys: '⎵ antenna → Void ⏎',
    heldIs: (h) => str(hd(h), 'id') !== undefined,
    label: (h) => `Delete webhook ${str(hd(h), 'id') ?? ''}`, build: (h) => ({ id: str(hd(h), 'id') ?? '' }) },

  // --- billing ---
  { id: 'usage', op: 'usage', row: 50, held: 'lens', target: 'meter', confirm: null, title: 'Read this month\'s use',
    gesture: 'Lens ⇢ Meter gauge', keys: 'Meter ⏎', label: () => 'Read this month\'s use against the plan', build: () => ({}) },
  { id: 'billing', op: 'billing', row: 51, held: 'lens', target: 'plan', confirm: null, title: 'Read the plan',
    gesture: 'Lens ⇢ Plan card', keys: 'Plan ⏎', label: () => 'Read the plan and subscription', build: () => ({}) },
  { id: 'checkout', op: 'checkout', row: 52, held: 'planTile', target: 'sigil', confirm: 'money', title: 'Buy this plan',
    gesture: 'Plan tile ⇢ workspace sigil (money confirm)', keys: '⎵ plan → sigil ⏎',
    label: (h) => `Buy the ${str(hd(h), 'plan') ?? '?'} plan`, build: (h) => ({ body: noUndefined({ plan: str(hd(h), 'plan') }) }) },
  { id: 'portal', op: 'portal', row: 53, held: 'sigil', target: 'gate:portal', confirm: 'money', title: 'Manage the subscription',
    gesture: 'Sigil ⇢ Portal gate (confirm)', keys: '⎵ sigil → Portal ⏎', label: () => 'Open the billing portal', build: () => ({}) },
  { id: 'mock-act', op: 'mockBillingAct', row: 54, held: 'mockAct', target: 'gate:mock', confirm: null, title: 'Play a mock billing act',
    gesture: 'Act chip ⇢ Mock turbine', keys: '⎵ chip → turbine ⏎',
    heldIs: (h) => !['cancel', 'fail'].includes(str(hd(h), 'act') ?? ''),
    label: (h) => `Mock ${str(hd(h), 'act') ?? '?'}${str(hd(h), 'plan') ? ` ${str(hd(h), 'plan')}` : ''}`,
    build: (h) => mockArgs(h) },
  { id: 'mock-act-confirm', op: 'mockBillingAct', row: 54, held: 'mockAct', target: 'gate:mock', confirm: 'destructive', title: 'Play a mock billing act (confirms)',
    gesture: 'cancel or fail chip ⇢ Mock turbine (confirms)', keys: '⎵ chip → turbine ⏎',
    heldIs: (h) => ['cancel', 'fail'].includes(str(hd(h), 'act') ?? ''),
    label: (h) => `Mock ${str(hd(h), 'act') ?? '?'}`, build: (h) => mockArgs(h) },
  { id: 'stripe-webhook', op: 'stripeWebhook', row: 55, held: 'card', target: 'gate:launch', confirm: 'server', title: 'Send the Stripe webhook',
    gesture: 'Deck only: card ⇢ Launch well (confirm: Stripe calls this, server to server)', keys: 'card ⌘⏎',
    heldIs: (h) => str(hd(h), 'op') === 'stripeWebhook',
    label: () => 'Send the Stripe webhook (Stripe calls this, server to server)', build: () => ({ body: {} }) },

  // --- the Director ---
  { id: 'assist', op: 'assist', row: 56, held: 'prompt', target: 'ground', confirm: null, title: 'Ask the Director',
    gesture: 'type in the Director, or fling the prompt card onto the stage', keys: '/ focus, ⏎ run, Esc stop',
    label: (h) => `Ask the Director: ${cut(str(hd(h), 'prompt') ?? '', 48)}`, build: (h, _t, ctx) => assistArgs(h, ctx) },
  { id: 'assist-director', op: 'assist', row: 56, held: 'prompt', target: 'gate:director', confirm: null, title: 'Ask the Director',
    gesture: 'prompt card ⇢ Director orb', keys: '/ focus, ⏎ run, Esc stop',
    label: (h) => `Ask the Director: ${cut(str(hd(h), 'prompt') ?? '', 48)}`, build: (h, _t, ctx) => assistArgs(h, ctx) },
  { id: 'assist-config', op: 'assistConfig', row: 57, held: 'lens', target: 'gate:director', confirm: null, title: 'Check the Director',
    gesture: 'Lens ⇢ Director orb (also on connect)', keys: 'orb ⏎ › Check',
    label: () => 'Check whether the Director is awake', build: () => ({}) },
];

function possibleRefusal(h: Held, act: string): string | null {
  const possible = strs(hd(h), 'possible');
  if (!possible) return null;
  return possible.includes(act) ? null : `possible acts: ${possible.join(', ') || 'none'}`;
}

function canRefusal(h: Held): string | null {
  const can = strs(hd(h), 'can') ?? ['see'];
  return can.includes('ask') && !can.includes('see') ? '"ask" needs "see"' : null;
}

function mockArgs(h: Held): Args {
  const plan = str(hd(h), 'plan');
  return plan ? { act: str(hd(h), 'act') ?? '', body: { plan } } : { act: str(hd(h), 'act') ?? '' };
}

function assistArgs(h: Held, ctx: VerbContext): Args {
  const o = hd(h);
  const focus = noUndefined({ world: ctx.focus.world, date: ctx.focus.date, hour: ctx.focus.hour });
  return {
    body: noUndefined({
      prompt: str(o, 'prompt'), mode: str(o, 'mode'), worlds: strs(o, 'worlds'), hands: strs(o, 'hands'),
      allowCreate: bool(o, 'allowCreate'), today: str(o, 'today'), focus: Object.keys(focus).length > 0 ? focus : undefined,
    }),
  };
}

// ---------- answer gates (§7 rule 9) ----------

export type AskVerdict = 'accept' | 'reject' | 'takeBack' | 'moveTo';

/**
 * Which verdicts do something to an ask in this state. `reject` after a yes
 * and `takeBack` on an open ask are no-ops the server still answers, so their
 * gates stay off.
 */
export const gatesFor = (state: string | null | undefined): readonly AskVerdict[] =>
  state === 'open' ? ['accept', 'reject', 'moveTo'] : state === 'accepted' ? ['takeBack', 'moveTo'] : [];

function askGate(h: Held, verdict: AskVerdict): string | null {
  const o = hd(h);
  const stale = str(o, 'stale');
  if (stale === 'stale') return 'may be stale: no relay lists this ask any more';
  if (stale === 'checking') return 'checking that a relay still lists this ask';
  const state = str(o, 'state') ?? null;
  if (gatesFor(state).includes(verdict)) return null;
  return `nothing to ${verdict} on an ask that is ${state ?? 'gone'}`;
}

function samePosition(h: Held, t: Target): string | null {
  const a = hd(h);
  const b = tgt(t);
  if (str(b, 'ask') !== undefined && str(b, 'ask') !== str(a, 'token')) return 'the row of another ask';
  if (str(a, 'date') !== str(b, 'date') || num(a, 'hour') !== num(b, 'hour')) return 'an ask moves only along its own row';
  return null;
}

/** How far the drop slides the spark: the slot under the pointer less the spark's first slot. */
export const shiftOf = (h: Held, t: Target): number => {
  const first = firstSlot(maskOf(str(hd(h), 'slots')));
  const slot = num(tgt(t), 'slot');
  return first < 0 || slot === undefined ? 0 : slot - first;
};

export const shiftMask = (mask: bigint, by: number): bigint => (by >= 0 ? mask << BigInt(by) : mask >> BigInt(-by));

function movedHex(h: Held, t: Target): string {
  return hex(shiftMask(maskOf(str(hd(h), 'slots')), shiftOf(h, t)));
}

function moveRefusal(h: Held, t: Target, ctx: VerbContext): string | null {
  const by = shiftOf(h, t);
  if (by === 0) return 'drop it where it asks to accept';
  const mask = maskOf(str(hd(h), 'slots'));
  const width = num(tgt(t), 'width') ?? (t.world ? ctx.planes.get(t.world)?.leafSlotBits : undefined) ?? 24;
  const moved = shiftMask(mask, by);
  const fits = moved >> BigInt(width) === 0n && moved !== 0n && firstSlot(moved) === firstSlot(mask) + by;
  return fits ? null : 'it does not fit the row there';
}

// ---------- the table, built ----------

/** Operations whose verdict model/predict.ts foresees from the planes in view: the dashed hint while carried (§3.1, §5.5 rule 8). */
const FORESEEN: ReadonlySet<string> = new Set(['claim', 'explainClaim', 'confirm', 'handover', 'release', 'giveUp', 'queue']);

const toVerb = (s: Spec, clock: () => number): HandVerb => {
  const { build, label, ...rest } = s;
  // A peer's commit landing mid-carry re-judges it, so the target can turn to `no` under the hand.
  const preview = rest.preview ?? (FORESEEN.has(s.op) ? (h: Held, t: Target, ctx: VerbContext) => predict(s.op, build(h, t, ctx, clock), ctx).verdict : undefined);
  return {
    ...rest,
    ...(preview ? { preview } : {}),
    label: (h, t) => `${label(h, t)} · ${s.op}`,
    args: (h, t, ctx) => complete(s.op, build(h, t, ctx, clock), ctx, s.variant),
  };
};

// ---------- the World's clock (§7 rule 17) ----------

const offsets: number[] = [];
const DAY_MS = 86_400_000;

/**
 * The server's clock as this tab sees it: `Date.now()` corrected by the
 * median of the last 15 offsets between an event's `at` (stamped by the
 * World) and the moment it arrived here. Screens that watch a World note
 * each event; a soft hold's `until` is read from this clock.
 */
export const worldClock = {
  /** An event the World stamped `at` arrived here at `now`. An offset over a day is a wrong unit, not a skew. */
  note(at: number, now: number = Date.now()): void {
    const off = at - now;
    if (!Number.isFinite(off) || Math.abs(off) > DAY_MS) return;
    offsets.push(off);
    if (offsets.length > 15) offsets.shift();
  },
  offset(): number {
    if (offsets.length === 0) return 0;
    const sorted = [...offsets].sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    return Math.round(sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2);
  },
  now: (): number => Date.now() + worldClock.offset(),
  reset(): void {
    offsets.length = 0;
  },
};

/** The curated verbs, with a clock for soft holds' `until` (the World's clock; tests pass a fixed one). */
export const createVerbs = (opts: { clock?: () => number } = {}): readonly HandVerb[] => {
  const clock = opts.clock ?? worldClock.now;
  return specs.map((s) => toVerb(s, clock));
};

/**
 * Rows §3.2 answers with an automatic path instead of a drop of its own.
 * The coverage test accepts a row that names one of these.
 */
export const AUTO_PATHS: Readonly<Record<string, string>> = {
  watch: 'the Tap ⇢ ring drop fires watchTicket, and the socket opens with that ticket (L toggles on a focused ring); the live links open watches on their own',
  relayWindows: 'lifting a braid over its planner reads the windows once per lift, then the AND-fold plays (the Loom)',
  sandboxConfig: 'pressing Try it now — no key asks whether a sandbox needs a human check first (Connect)',
  createSandbox: 'Try it now — no key makes a sandbox, after the human check on /signup where one is asked, and enters the stage with its key held in memory (Connect)',
  assistKeyCheck: 'checking the Anthropic key the user entered for the Director asks Anthropic once, and shows ✓ or ✕ with its words',
};

const launchVerb = (op: Operation, target: 'gate:launch' | 'gate:door', confirm: Verb['confirm'], heldIs: (h: Held) => boolean, suffix = ''): HandVerb => ({
  id: `${target === 'gate:door' ? 'door' : 'launch'}:${op.id}${suffix}`,
  op: op.id,
  row: 0,
  held: 'card',
  target,
  confirm,
  title: `Fire ${op.id} from its card`,
  gesture: `its card ⇢ ${target === 'gate:door' ? 'DOOR' : 'Launch well'}`,
  keys: 'card ⌘⏎',
  heldIs: (h) => str(hd(h), 'op') === op.id && heldIs(h),
  label: () => `Fire ${op.summary || op.id} · ${op.id}`,
  args: (h, t, ctx) => {
    const bound = objectOf(hd(h).args);
    const args: Record<string, Json> = { ...bound };
    if (t.world && op.path.startsWith('/v1/worlds/{w}') && bound.w === undefined) args.w = t.world;
    return complete(op.id, args, ctx);
  },
});

/**
 * The universal fallback: every operation of the catalogue as its card ⇢
 * Launch well, and a World operation's card ⇢ DOOR too. Built from the
 * catalogue, so a new operation in the spec is launchable with no code.
 */
export const launchVerbs = (catalogue: Catalogue): readonly HandVerb[] => {
  const out: HandVerb[] = [];
  const any = (): boolean => true;
  const loud = (h: Held): boolean => ['cancel', 'fail'].includes(str(objectOf(hd(h).args), 'act') ?? '');
  for (const op of catalogue.ops) {
    if (op.id === 'stripeWebhook') continue; // curated, row 55
    const targets: ('gate:launch' | 'gate:door')[] = op.path.startsWith('/v1/worlds/{w}') ? ['gate:launch', 'gate:door'] : ['gate:launch'];
    for (const target of targets) {
      if (op.id === 'mockBillingAct') {
        out.push(launchVerb(op, target, confirmOf(op, { act: 'complete' }), (h) => !loud(h)));
        out.push(launchVerb(op, target, confirmOf(op, { act: 'cancel' }), loud, '!'));
      } else {
        out.push(launchVerb(op, target, confirmOf(op, {}), any));
      }
    }
  }
  return out;
};

// ---------- matching ----------

const hv = (v: Verb): Partial<HandVerb> => v as Partial<HandVerb>;

/** The kinds meet, and each side is one this verb takes. */
export const fits = (v: Verb, h: Held, t: Target): boolean =>
  v.held === h.kind && v.target === t.kind && (hv(v).heldIs?.(h) ?? true) && (hv(v).targetIs?.(t) ?? true);

const modsHeld = (v: Verb, mods: ReadonlySet<Modifier>): boolean => (v.mods ?? []).every((m) => mods.has(m));
/** With several modifiers held, explain wins (nothing lights), then queue, then soft. */
const WEIGHT: Readonly<Record<Modifier, number>> = { explain: 4, queue: 2, soft: 1 };
const weight = (v: Verb): number => (v.mods ?? []).reduce((n, m) => n + WEIGHT[m], 0);
/** A verb that only asks the door: E's claim, or anything that is `explainClaim` (a drop on the DOOR). */
const explains = (v: Verb): boolean => (v.mods ?? []).includes('explain') || v.op === 'explainClaim';

/** Why E refuses a target that has nothing to explain. */
export const NOTHING_TO_EXPLAIN = 'nothing to explain here: E asks the door about a claim and lights nothing';

export type Choice = { readonly verb: Verb; readonly why: null } | { readonly verb: null; readonly why: string };

/**
 * The verb a drop of `h` on `t` fires, with these modifiers held: the one
 * that uses the most held modifiers and does not refuse. With E held only a
 * verb that asks the door may fire (§3.1: the door judges and nothing
 * lights), so E over anything else writes nothing. When none does, the
 * reason, in words.
 */
export const pick = (verbs: readonly Verb[], h: Held, t: Target, mods: ReadonlySet<Modifier>, ctx: VerbContext): Choice => {
  const kinds = verbs.filter((v) => fits(v, h, t));
  if (kinds.length === 0) return { verb: null, why: `${targetLabel(t)} does not take ${h.label}` };
  let usable = kinds.filter((v) => modsHeld(v, mods)).sort((a, b) => weight(b) - weight(a));
  if (usable.length === 0) return { verb: null, why: `hold ${kinds[0]?.mods?.join(' or ') ?? 'a modifier'} to drop here` };
  if (mods.has('explain')) {
    usable = usable.filter(explains);
    if (usable.length === 0) return { verb: null, why: NOTHING_TO_EXPLAIN };
  }
  let why: string | null = null;
  for (const v of usable) {
    const no = hv(v).refuse?.(h, t, ctx) ?? null;
    if (no === null) return { verb: v, why: null };
    why ??= no;
  }
  return { verb: null, why: why ?? `${targetLabel(t)} does not take ${h.label}` };
};

/** Every verb where the subject is the thing held or the thing dropped on. */
export const ringOf = (verbs: readonly Verb[], subject: Held | Target): readonly Verb[] => {
  const held = isHeld(subject);
  return verbs.filter((v) =>
    held
      ? v.held === subject.kind && (hv(v).heldIs?.(subject as Held) ?? true)
      : v.target === subject.kind && (hv(v).targetIs?.(subject as Target) ?? true));
};

export const isHeld = (x: Held | Target): x is Held => typeof (x as Held).label === 'string';

/** A target in words, for announcements and tooltips. */
export const targetLabel = (t: Target): string => {
  const o = objectOf(t.data);
  const label = str(o, 'label');
  if (label) return label;
  if (t.kind === 'bits') return words(posLabel(o), 'slots', slotsLabel(str(o, 'slots'))) || 'these bits';
  if (t.kind === 'row') return posLabel(o) || 'this row';
  if (t.kind.startsWith('gate:')) return `the ${t.kind.slice(5)} gate`;
  return t.world ? `${t.kind} of ${t.world}` : t.kind;
};

/** Things you hold without picking them from the World: a ring entry on a target fires them at once. */
export const TOOL_KINDS: ReadonlySet<HeldKind> = new Set<HeldKind>(['lens', 'needle', 'tap']);
